import React from 'react';
import { Wallet, Send, TrendingUp, TrendingDown, AlertCircle, Info, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  BOUTON_PIED_CLASSES,
  CENTRAL_MOVEMENT_TYPES,
  MODAL_CONTENT_CLASSES,
  formatMontant,
  parseMontant
} from '../nosDepensesUtils';

const USAGE_OPTIONS = [
  { value: 'budget_depenses', title: 'Budget dépenses', text: 'Finance les dépenses fixes et variables sans toucher aux encaissements.' },
  { value: 'principale', title: 'Caisse principale', text: 'Fonds de caisse exceptionnel, intégré dans la caisse principale.' }
];

/**
 * Mouvement manuel de la caisse centrale.
 *
 * Monté sur les primitives `Dialog` : le voile maison de `MODAL_OVERLAY_Z40_CLASS`
 * était rendu hors portail, donc neutralisé dès qu'un autre `Dialog` s'ouvrait.
 *
 * Le violet de la carte « solde après opération » cède la place à la teinte de
 * marque : DESIGN-SYSTEM.md ne réserve le violet qu'aux réductions
 * exceptionnelles.
 */
const MovementModal = ({ isOpen, onClose, form, setForm, employes, isLoading, onSubmit, soldeReelCentral = 0 }) => {
  const typeConfig = CENTRAL_MOVEMENT_TYPES.find((t) => t.value === form?.type);
  const isEntree = typeConfig?.sens === 'entree';
  const montant = parseMontant(form?.montant);
  const soldeApres = isEntree ? soldeReelCentral + montant : soldeReelCentral - montant;
  const soldeInsuffisant = !isEntree && montant > soldeReelCentral;
  const beneficiaire = employes?.find((e) => String(e.id) === String(form?.beneficiaire_id));

  return (
    <Dialog open={Boolean(isOpen)} onOpenChange={(ouvert) => !ouvert && !isLoading && onClose?.()}>
      <DialogContent className={`${MODAL_CONTENT_CLASSES} max-w-5xl`}>
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
            <Wallet size={18} />
          </div>
          <div className="min-w-0 flex-1 text-left">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Nouveau mouvement central
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600">
              Apport de la direction, dotation vers une caisse ouverte, salaire central ou ajustement contrôlé.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          <div className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4 text-sm text-gray-700">
            Les remises des gestionnaires sont créées automatiquement à la validation d'une caisse.
            Ce formulaire ne sert qu'aux mouvements manuels.
          </div>

          {/* Type de mouvement — cartes de choix, comme la sélection d'usage de dotation dans MaCaisse */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Type de mouvement <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {CENTRAL_MOVEMENT_TYPES.map((option) => {
                const optionEntree = option.sens === 'entree';
                const selected = form?.type === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setForm((prev) => ({
                      ...prev,
                      type: option.value,
                      beneficiaire_id: option.value === 'dotation' ? prev.beneficiaire_id : '',
                      usage_dotation: option.value === 'dotation' ? prev.usage_dotation : 'budget_depenses'
                    }))}
                    className={`flex items-start gap-3 rounded-lg border-2 p-3 text-left transition-colors ${selected
                      ? optionEntree ? 'border-emerald-400 bg-emerald-50' : 'border-[#c66d59] bg-[#f9f0ee]'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                  >
                    <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${optionEntree ? 'bg-emerald-100' : 'bg-[#f9f0ee]'}`}>
                      {optionEntree
                        ? <TrendingUp size={18} className="text-emerald-600" />
                        : <TrendingDown size={18} className="text-[#c66d59]" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{option.label}</p>
                      <p className={`mt-0.5 text-xs font-medium ${optionEntree ? 'text-emerald-700' : 'text-[#ae7467]'}`}>
                        {optionEntree ? 'Entrée — crédite le solde' : 'Sortie — débite le solde'}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Bénéficiaire + usage (dotation uniquement) */}
          {form?.type === 'dotation' && (
            <div className="rounded-lg border-2 border-[#246990]/20 bg-[#eef7fb]/40">
              <div className="border-b border-[#246990]/20 bg-[#eef7fb] p-4">
                <h4 className="text-base font-semibold text-[#246990]">Destination de la dotation</h4>
                <p className="mt-1 text-sm text-gray-600">La caisse du bénéficiaire doit être ouverte aujourd'hui.</p>
              </div>
              <div className="space-y-4 p-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Caisse / personne bénéficiaire <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={form.beneficiaire_id || undefined}
                    onValueChange={(value) => setForm((prev) => ({ ...prev, beneficiaire_id: value }))}
                  >
                    <SelectTrigger className="border-gray-300 bg-white">
                      <SelectValue placeholder="Sélectionner un bénéficiaire" />
                    </SelectTrigger>
                    <SelectContent>
                      {employes?.map((employe) => (
                        <SelectItem key={employe.id} value={String(employe.id)}>
                          {[employe.prenom, employe.nom].filter(Boolean).join(' ') || `Employé #${employe.id}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Usage de la dotation</label>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {USAGE_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setForm((prev) => ({ ...prev, usage_dotation: option.value }))}
                        className={`rounded-lg border-2 p-3 text-left transition-colors ${form.usage_dotation === option.value
                          ? 'border-[#246990] bg-[#eef7fb]'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                      >
                        <p className="text-sm font-semibold text-gray-900">{option.title}</p>
                        <p className="mt-1 text-xs text-gray-600">{option.text}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Montant + impact sur le solde */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Montant <span className="text-red-500">*</span>
              </label>
              <div className="flex">
                <Input
                  type="number"
                  min="0"
                  step="1000"
                  value={form?.montant || ''}
                  onChange={(e) => setForm((prev) => ({ ...prev, montant: e.target.value }))}
                  placeholder="0"
                  className="rounded-r-none"
                />
                <span className="inline-flex items-center rounded-r-lg border border-l-0 border-gray-300 bg-gray-50 px-3 text-sm font-medium text-gray-600">
                  FCFA
                </span>
              </div>
            </div>

            <div className={`rounded-lg border-2 p-3 ${soldeInsuffisant ? 'border-red-200 bg-red-50' : 'border-[#246990]/20 bg-[#eef7fb]'}`}>
              <p className={`text-xs font-semibold uppercase ${soldeInsuffisant ? 'text-red-700' : 'text-[#246990]'}`}>
                Solde central après opération
              </p>
              <p className={`mt-1 text-xl font-bold tabular-nums ${soldeInsuffisant ? 'text-red-700' : 'text-[#246990]'}`}>
                {formatMontant(soldeApres)}
              </p>
              <p className="mt-1 text-xs text-gray-600">
                Actuel {formatMontant(soldeReelCentral)} {montant > 0 && (isEntree ? `+ ${formatMontant(montant)}` : `− ${formatMontant(montant)}`)}
              </p>
            </div>
          </div>

          {soldeInsuffisant && (
            <div className="rounded border-l-4 border-red-500 bg-red-50 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="mt-0.5 flex-shrink-0 text-red-600" />
                <div>
                  <h5 className="mb-1 font-semibold text-red-900">Solde central insuffisant</h5>
                  <p className="text-sm text-red-800">
                    Cette sortie de {formatMontant(montant)} dépasse le solde validé de {formatMontant(soldeReelCentral)}.
                    L'opération sera refusée par le serveur.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Libellé</label>
              <Input
                value={form?.libelle || ''}
                onChange={(e) => setForm((prev) => ({ ...prev, libelle: e.target.value }))}
                placeholder="Ex : dotation salaires, apport direction, achat exceptionnel…"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Description / motif</label>
              <textarea
                value={form?.description || ''}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Précisez pourquoi ce mouvement est enregistré."
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
              />
            </div>
          </div>

          {/* Récapitulatif avant enregistrement */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-start gap-3">
              <Info size={18} className="mt-0.5 flex-shrink-0 text-gray-500" />
              <div className="space-y-1 text-sm text-gray-700">
                <p>
                  <strong>{typeConfig?.label || 'Mouvement'}</strong> de <strong>{formatMontant(montant)}</strong>
                  {isEntree ? ' entrant en caisse centrale' : ' sortant de la caisse centrale'}
                  {form?.type === 'dotation' && beneficiaire && (
                    <> vers <strong>{[beneficiaire.prenom, beneficiaire.nom].filter(Boolean).join(' ')}</strong></>
                  )}
                  .
                </p>
                <p className="text-xs text-gray-500">
                  Pour payer salaires et dépenses courantes, créez d'abord une dotation vers la caisse responsable
                  plutôt qu'une sortie centrale directe.
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="border-t bg-gray-50 px-5 py-4">
          <Button
            variant="outline"
            className={BOUTON_PIED_CLASSES}
            onClick={onClose}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            className={`${BOUTON_PIED_CLASSES} bg-[#246990] text-white hover:bg-[#1f5a7a]`}
            onClick={onSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Enregistrement…
              </>
            ) : (
              <>
                <Send size={16} />
                Enregistrer
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MovementModal;
